import React, { createContext, useContext, useEffect, useState } from 'react'
import { useBranding } from '../hooks/useBranding'
import { BrandingContextValue } from '../types/branding'

// AIFM brand colors for admin portals
const AIFM_BRAND = {
  primary: '#22D3EE',     // Cyan
  primaryRgb: '34, 211, 238',  // RGB values for rgba()
  secondary: '#06B6D4',   // Darker cyan
  accent: '#4ADE80',      // Green
}

// Helper functions for color manipulation
function hexToRgb(hex: string): string {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
  if (!result) return '34, 211, 238'
  return `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`
}

function adjustColor(hex: string, percent: number): string {
  const num = parseInt(hex.replace('#', ''), 16)
  const amt = Math.round(2.55 * percent)
  const R = Math.min(255, Math.max(0, (num >> 16) + amt))
  const G = Math.min(255, Math.max(0, ((num >> 8) & 0x00FF) + amt))
  const B = Math.min(255, Math.max(0, (num & 0x0000FF) + amt))
  return '#' + (0x1000000 + R * 0x10000 + G * 0x100 + B).toString(16).slice(1)
}

const BrandingContext = createContext<BrandingContextValue | undefined>(undefined)

// Helper to check if current path is customer portal
function isCustomerPortalPath(): boolean {
  const path = window.location.pathname
  return path.startsWith('/customer') || path.startsWith('/store')
}

export function BrandingProvider({ children }: { children: React.ReactNode }) {
  const brandingHook = useBranding()
  const [isCustomerPortal, setIsCustomerPortal] = useState(isCustomerPortalPath)

  // Update on path changes
  useEffect(() => {
    const checkPath = () => setIsCustomerPortal(isCustomerPortalPath())
    window.addEventListener('popstate', checkPath)
    // Check periodically for SPA navigation
    const interval = setInterval(checkPath, 500)
    return () => {
      window.removeEventListener('popstate', checkPath)
      clearInterval(interval)
    }
  }, [])

  // Apply branding to document when it changes
  useEffect(() => {
    const root = document.documentElement

    console.log('🎨 BrandingContext useEffect running:', {
      isCustomerPortal,
      hasBranding: !!brandingHook.branding,
      brandingPrimaryColor: brandingHook.branding?.primary_color
    })

    // For admin portals, use org branding if available, otherwise AIFM defaults
    if (!isCustomerPortal) {
      // Check if we have organization branding
      if (brandingHook.branding) {
        const { branding } = brandingHook
        const primaryColor = branding.primary_color || AIFM_BRAND.primary
        const secondaryColor = branding.secondary_color || AIFM_BRAND.secondary
        const accentColor = branding.accent_color || AIFM_BRAND.accent

        // Set all color variables - both new and legacy names
        root.style.setProperty('--color-primary', primaryColor)
        root.style.setProperty('--color-secondary', secondaryColor)
        root.style.setProperty('--color-accent', accentColor)
        root.style.setProperty('--color-gold-400', primaryColor)
        root.style.setProperty('--color-gold-500', secondaryColor)
        root.style.setProperty('--color-gold-600', accentColor)
        root.style.setProperty('--primary-color', primaryColor)
        root.style.setProperty('--primary-hover', secondaryColor)
        root.style.setProperty('--primary-light', adjustColor(primaryColor, 20))
        const primaryRgb = hexToRgb(primaryColor)
        console.log('🎨 BrandingContext setting --primary-rgb:', primaryRgb, 'from primaryColor:', primaryColor)
        root.style.setProperty('--primary-rgb', primaryRgb)

        // Critical: Also set --accent which many components use
        root.style.setProperty('--accent', primaryColor)
        root.style.setProperty('--accent-cyan', primaryColor)
        root.style.setProperty('--accent-glow', `rgba(${hexToRgb(primaryColor)}, 0.2)`)
        root.style.setProperty('--accent-cyan-dim', `rgba(${hexToRgb(primaryColor)}, 0.1)`)
        root.style.setProperty('--border-hover', `rgba(${hexToRgb(primaryColor)}, 0.3)`)
        root.style.setProperty('--info', primaryColor)
        root.style.setProperty('--info-bg', `rgba(${hexToRgb(primaryColor)}, 0.15)`)
        root.style.setProperty('--info-border', `rgba(${hexToRgb(primaryColor)}, 0.3)`)

        // Calendar-specific CSS variables for fullcalendar-theme-dynamic.css
        root.style.setProperty('--accent-transparent', `rgba(${hexToRgb(primaryColor)}, 0.1)`)
        root.style.setProperty('--accent-border', `rgba(${hexToRgb(primaryColor)}, 0.3)`)
        root.style.setProperty('--accent-hover', `rgba(${hexToRgb(primaryColor)}, 0.2)`)

        // Spotlight/background gradient colors
        root.style.setProperty('--spotlight-glow', `rgba(${hexToRgb(primaryColor)}, 0.15)`)
        root.style.setProperty('--spotlight-glow-strong', `rgba(${hexToRgb(primaryColor)}, 0.2)`)

        // Glow colors for calendar and components
        root.style.setProperty('--glow-cyan', `0 0 15px rgba(${hexToRgb(primaryColor)}, 0.2)`)
        root.style.setProperty('--glow-cyan-strong', `0 0 25px rgba(${hexToRgb(primaryColor)}, 0.4)`)

        // Set comprehensive CSS variables for components
        root.style.setProperty('--org-primary', branding.primary_color || AIFM_BRAND.primary)
        const orgPrimaryRgb = hexToRgb(branding.primary_color || AIFM_BRAND.primary)
        console.log('🎨 BrandingContext setting --org-primary-rgb:', orgPrimaryRgb, 'from branding.primary_color:', branding.primary_color)
        root.style.setProperty('--org-primary-rgb', orgPrimaryRgb)
        root.style.setProperty('--org-primary-dark', adjustColor(branding.primary_color || AIFM_BRAND.primary, -15))
        root.style.setProperty('--org-primary-light', adjustColor(branding.primary_color || AIFM_BRAND.primary, 15))
        root.style.setProperty('--org-background', '#080a0f')  // Keep dark admin background
        root.style.setProperty('--org-background-deep', '#050709')
        root.style.setProperty('--org-text', '#f8fafc')
        root.style.setProperty('--org-text-light', '#94a3b8')

        document.title = branding.company_name || 'AI Facility Manager'
      } else {
        // Fallback to AIFM brand
        root.style.setProperty('--color-primary', AIFM_BRAND.primary)
        root.style.setProperty('--color-secondary', AIFM_BRAND.secondary)
        root.style.setProperty('--color-accent', AIFM_BRAND.accent)
        root.style.setProperty('--color-gold-400', AIFM_BRAND.primary)
        root.style.setProperty('--color-gold-500', AIFM_BRAND.secondary)
        root.style.setProperty('--color-gold-600', AIFM_BRAND.accent)
        root.style.setProperty('--primary-color', AIFM_BRAND.primary)
        root.style.setProperty('--primary-hover', AIFM_BRAND.secondary)
        root.style.setProperty('--primary-light', '#67E8F9')
        root.style.setProperty('--primary-rgb', AIFM_BRAND.primaryRgb)

        // Also set --accent variables for AIFM fallback
        root.style.setProperty('--accent', AIFM_BRAND.primary)
        root.style.setProperty('--accent-cyan', AIFM_BRAND.primary)
        root.style.setProperty('--accent-glow', `rgba(${AIFM_BRAND.primaryRgb}, 0.2)`)
        root.style.setProperty('--accent-cyan-dim', `rgba(${AIFM_BRAND.primaryRgb}, 0.1)`)

        // Calendar-specific CSS variables for fullcalendar-theme-dynamic.css
        root.style.setProperty('--accent-transparent', `rgba(${AIFM_BRAND.primaryRgb}, 0.1)`)
        root.style.setProperty('--accent-border', `rgba(${AIFM_BRAND.primaryRgb}, 0.3)`)
        root.style.setProperty('--accent-hover', `rgba(${AIFM_BRAND.primaryRgb}, 0.2)`)

        // Spotlight/background gradient colors
        root.style.setProperty('--spotlight-glow', `rgba(${AIFM_BRAND.primaryRgb}, 0.15)`)
        root.style.setProperty('--spotlight-glow-strong', `rgba(${AIFM_BRAND.primaryRgb}, 0.2)`)

        // Glow colors for calendar and components
        root.style.setProperty('--glow-cyan', `0 0 15px rgba(${AIFM_BRAND.primaryRgb}, 0.2)`)
        root.style.setProperty('--glow-cyan-strong', `0 0 25px rgba(${AIFM_BRAND.primaryRgb}, 0.4)`)

        // Set CSS variables for components using defaults
        root.style.setProperty('--org-primary', AIFM_BRAND.primary)
        console.log('🎨 BrandingContext FALLBACK setting --org-primary-rgb:', AIFM_BRAND.primaryRgb)
        root.style.setProperty('--org-primary-rgb', AIFM_BRAND.primaryRgb)
        root.style.setProperty('--org-primary-dark', '#00d4e0')
        root.style.setProperty('--org-primary-light', '#67E8F9')
        root.style.setProperty('--org-background', '#080a0f')
        root.style.setProperty('--org-background-deep', '#050709')
        root.style.setProperty('--org-text', '#f8fafc')
        root.style.setProperty('--org-text-light', '#94a3b8')

        document.title = 'AI Facility Manager'
      }
      return
    }

    // For customer portal, use organization branding
    if (brandingHook.branding) {
      const { branding } = brandingHook

      // Update CSS custom properties for theme colors
      root.style.setProperty('--color-primary', branding.primary_color)
      root.style.setProperty('--color-secondary', branding.secondary_color)
      root.style.setProperty('--color-accent', branding.accent_color || branding.primary_color)
      root.style.setProperty('--color-gold-400', branding.primary_color)
      root.style.setProperty('--color-gold-500', branding.secondary_color)
      root.style.setProperty('--color-gold-600', branding.accent_color || branding.primary_color)
      root.style.setProperty('--primary-color', branding.primary_color)
      root.style.setProperty('--primary-hover', branding.secondary_color)

      // Update font family
      if (branding.font_family) {
        root.style.setProperty('--font-family', branding.font_family)
        document.body.style.fontFamily = branding.font_family
      }

      // Update favicon if provided
      if (branding.favicon_url) {
        const favicon = document.querySelector('link[rel="icon"]') as HTMLLinkElement
        if (favicon) {
          favicon.href = branding.favicon_url
        } else {
          const newFavicon = document.createElement('link')
          newFavicon.rel = 'icon'
          newFavicon.href = branding.favicon_url
          document.head.appendChild(newFavicon)
        }
      }

      // Update page title with company name
      const baseTitle = document.title.split(' - ')[0]
      document.title = `${baseTitle} - ${branding.company_name}`

      // Update meta description
      const metaDescription = document.querySelector('meta[name="description"]')
      if (metaDescription && branding.company_name) {
        metaDescription.setAttribute('content',
          `${branding.company_name} - Professional facility management system for sports and recreation centers.`
        )
      }
    }
  }, [brandingHook.branding, isCustomerPortal])

  return (
    <BrandingContext.Provider value={brandingHook}>
      {children}
    </BrandingContext.Provider>
  )
}

export function useBrandingContext(): BrandingContextValue {
  const context = useContext(BrandingContext)
  if (context === undefined) {
    throw new Error('useBrandingContext must be used within a BrandingProvider')
  }
  return context
}

// Component to inject branding styles (only for customer portal)
export function BrandingStyleInjector() {
  const { branding } = useBrandingContext()
  const [isCustomerPortal, setIsCustomerPortal] = useState(isCustomerPortalPath)

  useEffect(() => {
    const checkPath = () => setIsCustomerPortal(isCustomerPortalPath())
    window.addEventListener('popstate', checkPath)
    const interval = setInterval(checkPath, 500)
    return () => {
      window.removeEventListener('popstate', checkPath)
      clearInterval(interval)
    }
  }, [])

  // For admin portals, don't inject organization branding - use AIFM brand
  if (!isCustomerPortal || !branding) return null

  return (
    <style>
      {`
        :root {
          --color-primary: ${branding.primary_color};
          --color-secondary: ${branding.secondary_color};
          --color-accent: ${branding.accent_color || branding.primary_color};
          --color-gold-400: ${branding.primary_color};
          --color-gold-500: ${branding.secondary_color};
          --color-gold-600: ${branding.accent_color || branding.primary_color};
          --primary-color: ${branding.primary_color};
          --primary-hover: ${branding.secondary_color};
          --font-family: ${branding.font_family};
        }

        body {
          font-family: ${branding.font_family};
        }

        .text-primary-brand {
          color: ${branding.primary_color};
        }

        .text-secondary-brand {
          color: ${branding.secondary_color};
        }

        .text-accent-brand {
          color: ${branding.accent_color || branding.primary_color};
        }

        .bg-primary-brand {
          background-color: ${branding.primary_color};
        }

        .bg-secondary-brand {
          background-color: ${branding.secondary_color};
        }

        .bg-accent-brand {
          background-color: ${branding.accent_color || branding.primary_color};
        }

        .border-primary-brand {
          border-color: ${branding.primary_color};
        }

        .border-secondary-brand {
          border-color: ${branding.secondary_color};
        }

        .border-accent-brand {
          border-color: ${branding.accent_color || branding.primary_color};
        }

        /* Update existing gold classes to use branding colors */
        .text-gold-400 {
          color: ${branding.primary_color} !important;
        }

        .text-gold-500 {
          color: ${branding.secondary_color} !important;
        }

        .text-gold-600 {
          color: ${branding.accent_color || branding.primary_color} !important;
        }

        .bg-gold-400 {
          background-color: ${branding.primary_color} !important;
        }

        .bg-gold-500 {
          background-color: ${branding.secondary_color} !important;
        }

        .border-gold-400 {
          border-color: ${branding.primary_color} !important;
        }

        .border-gold-500 {
          border-color: ${branding.secondary_color} !important;
        }

        .border-gold-600 {
          border-color: ${branding.accent_color || branding.primary_color} !important;
        }
        
        /* Hover effects with branding colors */
        .hover\\:bg-gold-500:hover {
          background-color: ${branding.secondary_color} !important;
        }
        
        .hover\\:text-gold-300:hover {
          color: ${branding.accent_color || branding.primary_color} !important;
        }
        
        .hover\\:border-gold-500:hover {
          border-color: ${branding.secondary_color} !important;
        }
      `}
    </style>
  )
}